import axios from 'axios'

import { createFormData, getApiHeaders } from '../utils/api_helper'
import { addToast, loadTooltips, pluralize } from '../utils/common'

import { configureDragAndDrop } from './drag_and_drop'

interface IErrorResponse {
  response?: {
    status?: number
    statusText?: string
    data?: unknown
  }
}

interface IAttachmentConfig {
  renderNoAttachmentPath: string
  renderUploadedPath: string
  deleteAttachmentPath: string
  createAttachmentPath: string
  pendingAttachmentPath: string
  resyncAttachmentPath?: string
  canEdit?: boolean
}
//
// Attachments drag-and-drop
//
let clickUploadAll = false
const getPendingAttachmentCount = () => {
  return document.querySelectorAll('td>div.spinner').length
}

const getPendingUploadAttachmentCount = () => {
  return document.querySelectorAll('.hyper-attachment-pending-upload').length
}

const getLastPendingAttachmentTr = () => {
  const spinners = document.querySelectorAll('td>div.spinner')
  if (spinners) {
    const lastSpinner = spinners[spinners.length - 1]
    return lastSpinner.parentElement?.closest('tr')
  }
  return null
}

export const renderPendingAlert = (pendingCount: number) => {
  const createAlert = (message: string) => {
    const alert = document.createElement('div')
    alert.innerHTML = `<div class="alert alert-danger" role="alert">${message}</div>`
    return alert
  }

  const alertContainer = document.querySelector('div.list-alert')
  if (alertContainer) {
    while (alertContainer.firstChild) {
      alertContainer.removeChild(alertContainer.lastChild as Element)
    }
    if (pendingCount > 0) {
      const message = `${pluralize(pendingCount, 'pending upload')}. Please select ${pendingCount === 1 ? 'a category' : 'categories'}.`
      alertContainer.insertBefore(
        createAlert(message),
        alertContainer.firstChild,
      )
    }
  }
}

const renderPendingUploadAlert = (acceptedCount = 0) => {
  const createAlert = (message: string) => {
    const alert = document.createElement('div')
    alert.innerHTML = `<div class="alert alert-danger" role="alert">${message}</div>`
    return alert
  }

  const alertContainer = document.querySelector('div.list-alert')
  if (alertContainer) {
    while (alertContainer.firstChild) {
      alertContainer.removeChild(alertContainer.lastChild as Element)
    }

    const pendingCount = getPendingUploadAttachmentCount() + acceptedCount
    if (pendingCount > 0) {
      const message = `${pluralize(pendingCount, 'attachment')} pending upload. Please select 'Upload Now' to continue.`
      alertContainer.insertBefore(
        createAlert(message),
        alertContainer.firstChild,
      )
    }
  }
}

const renderSuccessAlert = (message: string) => {
  const createAlert = (message: string) => {
    const alert = document.createElement('div')
    alert.innerHTML = `
      <div class="alert alert-success alert-dismissible" role="alert">
        ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>`
    return alert
  }

  const alertContainer = document.querySelector('div.list-alert')
  if (alertContainer) {
    while (alertContainer.firstChild) {
      alertContainer.removeChild(alertContainer.lastChild as Element)
    }
    alertContainer.insertBefore(createAlert(message), alertContainer.firstChild)
  }
}

export const getNoAttachmentHtml = async (path: string) => {
  try {
    const response = await axios.get(path)
    return response.data
  } catch (e) {
    console.error(e)
    addToast('Oops, something went wrong. Please refresh the page.')
  }

  return null
}

export const renderNoAttachment = async (
  tbody: HTMLTableSectionElement,
  path: string,
) => {
  const noAttachmentHtml = await getNoAttachmentHtml(path)
  if (noAttachmentHtml) {
    const noAttachmentTr = document.createElement('tr')
    noAttachmentTr.innerHTML = noAttachmentHtml
    tbody.insertBefore(noAttachmentTr, tbody.firstChild)
  }
}

const deleteAttachment = async (attachmentId: string, path: string) => {
  const deleteAttachmentEndpoint = path + `${attachmentId}/`
  try {
    await axios.delete(deleteAttachmentEndpoint, { headers: getApiHeaders() })
    renderSuccessAlert('Attachment has successfully been deleted')
    return true
  } catch (e: unknown) {
    console.error(e)
    const error = e as IErrorResponse
    const statusCode = error.response?.status || 'Unknown'
    const statusText = error.response?.statusText || 'Unknown error'
    addToast(
      `Unable to delete attachment.\nStatus Code: ${statusCode}\nMessage: ${statusText}`,
    )
  }

  return false
}

const configureConfirmAttachmentDeleteModal = (
  el: Element,
  config: IAttachmentConfig,
) => {
  el.querySelector('button[type="submit"]')?.addEventListener(
    'click',
    async (e) => {
      e.preventDefault()

      const attachmentId = el.getAttribute('id')?.split('-').at(-1)
      if (
        attachmentId &&
        (await deleteAttachment(attachmentId, config.deleteAttachmentPath))
      ) {
        const deletedAttachmentTr = el.parentElement?.closest('tr')
        if (deletedAttachmentTr) {
          const tbody = deletedAttachmentTr.parentElement?.closest('tbody')
          if (tbody && tbody.children.length <= 1) {
            await renderNoAttachment(tbody, config.renderNoAttachmentPath)
          }
          deletedAttachmentTr.remove()
        }
      }
    },
  )
}
const resyncAttachment = async (attachmentId: string, path: string) => {
  const resyncAttachmentEndpoint = path + `${attachmentId}/`
  try {
    await axios.post(resyncAttachmentEndpoint, {}, { headers: getApiHeaders() })
    renderSuccessAlert(
      'Your attachment has been queued and will be sent to the provider once completed.',
    )
    return true
  } catch (e: unknown) {
    console.error(e)
    addToast(
      'Unable to resync attachment. Please refresh the page and try again.',
    )
  }

  return false
}

const configureResyncAttachment = (
  el: Element,
  resyncAttachmentPath: string,
) => {
  el.addEventListener('click', async () => {
    const attachmentId = el.getAttribute('id')?.split('-').at(-1)
    if (
      attachmentId &&
      (await resyncAttachment(attachmentId, resyncAttachmentPath))
    ) {
      const span = el.parentElement?.closest('span')
      el.setAttribute('disabled', 'disabled')
      span?.setAttribute(
        'data-bs-original-title',
        'Please refresh the page to check the attachment status.',
      )
    }
  })
}
const getUploadedAttachmentHtml = async (
  attachmentId: string,
  uploadDate: string,
  filename: string,
  filesize: number,
  downloadUrl: string,
  category: string,
  path: string,
  canDelete: boolean,
) => {
  try {
    const response = await axios.get(path, {
      params: {
        id: attachmentId,
        upload_date: uploadDate,
        filename,
        filesize,
        download_url: downloadUrl,
        category,
        can_delete: canDelete,
      },
    })
    return response.data
  } catch (e) {
    console.error(e)
    addToast('Oops, something went wrong. Please refresh the page.')
  }
  return null
}

const renderUploadedAttachment = async (
  uploadDate: string,
  filename: string,
  filesize: number,
  downloadUrl: string,
  category: string,
  pendingAttachmentTr: HTMLTableRowElement,
  config: IAttachmentConfig,
  canDelete: boolean,
) => {
  try {
    const attachmentId = downloadUrl.split('/').at(-2)
    if (attachmentId) {
      const uploadedAttachmentHtml = await getUploadedAttachmentHtml(
        attachmentId,
        uploadDate,
        filename,
        filesize,
        downloadUrl,
        category,
        config.renderUploadedPath,
        canDelete,
      )
      if (uploadedAttachmentHtml) {
        const uploadedAttachmentTr = document.createElement('tr')
        uploadedAttachmentTr.innerHTML = uploadedAttachmentHtml
        const confirmAttachmentDeleteModal = uploadedAttachmentTr.querySelector(
          '[id^="confirm-attachment-delete-modal-"]',
        )
        if (confirmAttachmentDeleteModal) {
          configureConfirmAttachmentDeleteModal(
            confirmAttachmentDeleteModal,
            config,
          )
        }
        const resyncButton = uploadedAttachmentTr.querySelector('.sync-button')
        const resyncSpan = resyncButton?.parentElement?.closest('span')
        resyncButton?.setAttribute('disabled', 'disabled')
        resyncSpan?.setAttribute(
          'title',
          'Please refresh the page to check the attachment status.',
        )
        loadTooltips(uploadedAttachmentTr)
        const tbody = pendingAttachmentTr.parentElement?.closest('tbody')
        if (tbody) {
          const lastPendingAttachmentTr = getLastPendingAttachmentTr()
          tbody.insertBefore(
            uploadedAttachmentTr,
            lastPendingAttachmentTr!.nextSibling,
          )
        }
        pendingAttachmentTr.remove()
        renderPendingAlert(getPendingAttachmentCount())
      }
    }
    return true
  } catch (e: unknown) {
    console.error(e)
    addToast('Unable to upload attachment. Please try again.')
    return false
  }
}

const createAttachment = async (file: File, category: string, path: string) => {
  try {
    const response = await axios.post(
      path,
      createFormData({
        attachment: file,
        category,
      }),
      {
        headers: getApiHeaders(),
      },
    )
    return response.data
  } catch (e: unknown) {
    console.error(e)

    const defaultMessage = 'Unable to upload attachment. Please try again.'
    const error = e as IErrorResponse

    if (!error.response) {
      addToast(defaultMessage)
      return null
    }

    if (error.response.status === 409) {
      addToast(
        'A file with name ' +
          (error.response?.data || 'unknown') +
          ' already exists',
      )
    } else if (
      error.response.status === 400 &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'attachment' in error.response.data
    ) {
      const attachmentData = error.response.data as { attachment: string[] }
      addToast(attachmentData.attachment[0])
    } else {
      addToast(defaultMessage)
    }
  }

  return null
}

const uploadAttachment = async (
  file: File,
  category: string,
  select: HTMLSelectElement | Element,
  config: IAttachmentConfig,
) => {
  const pendingAttachmentTr = select.parentElement?.closest(
    'tr',
  ) as HTMLTableRowElement
  const buttonRemove = pendingAttachmentTr.querySelector(
    '.hyper-btn-remove-attachment',
  ) as HTMLButtonElement
  const spinner = pendingAttachmentTr.querySelector(
    'div.spinner',
  ) as HTMLElement

  const uploadStart = () => {
    select.setAttribute('disabled', 'true')
    buttonRemove.style.display = 'none'
    spinner.style.display = 'inline-block'
  }

  const uploadComplete = (
    createdAttachment: unknown,
    uploadedAttachment: unknown,
  ) => {
    select.setAttribute('disabled', 'false')
    spinner.style.display = 'none'
    buttonRemove.style.display = 'inline-block'
    if (createdAttachment && uploadedAttachment) {
      renderSuccessAlert('Attachment uploaded successfully')
    }
  }

  uploadStart()
  const createdAttachment = await createAttachment(
    file,
    category,
    config.createAttachmentPath,
  )
  let uploadedAttachment
  if (createdAttachment) {
    uploadedAttachment = await renderUploadedAttachment(
      createdAttachment.upload_date,
      createdAttachment.filename,
      createdAttachment.filesize,
      createdAttachment.download_url,
      createdAttachment.category_display,
      pendingAttachmentTr,
      config,
      createdAttachment.can_delete,
    )
  }
  uploadComplete(createdAttachment, uploadedAttachment)
  if (config.canEdit) {
    if (config.resyncAttachmentPath) {
      document.querySelectorAll('.sync-button').forEach((element) => {
        const span = element.parentElement?.closest('span')
        element.setAttribute('disabled', 'disabled')
        span?.setAttribute(
          'title',
          "This attachment hasn't been sent to the provider. Application will need to be completed first.",
        )
      })
    }
  }
}

const getPendingUploadHtml = async (
  filename: string,
  filesize: number,
  category: string,
  path: string,
) => {
  try {
    const response = await axios.get(path, {
      params: {
        filename,
        filesize,
        category,
      },
    })
    return response.data
  } catch (e) {
    console.error(e)
    addToast('Oops, something went wrong. Please try again.')
  }

  return null
}

const resetUploadAll = () => {
  // Conditionally clear up the 'Upload All' button
  const buttonUploadAll = document.querySelector(
    '.hyper-btn-upload-all',
  ) as HTMLButtonElement
  if (buttonUploadAll) {
    const buttonsUpload = document.querySelectorAll(
      '.hyper-btn-upload-attachment',
    )
    if (buttonsUpload.length === 0) {
      buttonUploadAll.style.display = 'none'
      clickUploadAll = false
    } else {
      buttonUploadAll.removeAttribute('disabled')
    }
  }
}

const handleUploadAll = async () => {
  clickUploadAll = true
  const buttonsUpload = document.querySelector('.hyper-btn-upload-attachment')
  await (buttonsUpload as HTMLButtonElement).click()
}

const handlePendingUploadAll = () => {
  const buttonUploadAll = document.querySelector(
    '.hyper-btn-upload-all',
  ) as HTMLButtonElement
  if (buttonUploadAll) {
    buttonUploadAll.style.display = 'inline-block'
    buttonUploadAll.removeAttribute('disabled')
    buttonUploadAll.addEventListener('click', () => {
      buttonUploadAll.setAttribute('disabled', 'true')
      handleUploadAll().then(() => {
        resetUploadAll()
      })
    })
  }
}

const configureButtonRemove = (button: Element, config: IAttachmentConfig) => {
  button?.addEventListener('click', () => {
    const pendingAttachmentTr = button.parentElement?.closest('tr')
    if (pendingAttachmentTr) {
      const tbody = pendingAttachmentTr.parentElement?.closest('tbody')
      if (tbody && tbody.children.length <= 1) {
        renderNoAttachment(tbody, config.renderNoAttachmentPath)
      }
      pendingAttachmentTr.remove()
      renderPendingAlert(getPendingAttachmentCount())
      resetUploadAll()
    }
  })
}

const configureButtonUpload = (
  button: Element,
  file: File,
  category: string,
  config: IAttachmentConfig,
  pendingAttachmentTr: HTMLTableRowElement,
) => {
  button?.addEventListener('click', () => {
    const select = pendingAttachmentTr.querySelector('select')
    if (select) {
      category = select.value
    }

    const buttonUploadAll = document.querySelector(
      '.hyper-btn-upload-all',
    ) as HTMLButtonElement
    buttonUploadAll?.setAttribute('disabled', 'true')
    uploadAttachment(file, category, button, config).then(async () => {
      const buttonsUpload = document.querySelector(
        '.hyper-btn-upload-attachment',
      )
      if (clickUploadAll && buttonsUpload != null) {
        await (buttonsUpload as HTMLButtonElement).click()
      }
      resetUploadAll()
    })
  })
}

const configurePendingUploadAttachmentTr = (
  pendingAttachmentTr: HTMLTableRowElement,
  file: File,
  category: string,
  config: IAttachmentConfig,
) => {
  const buttonRemove = pendingAttachmentTr.querySelector(
    '.hyper-btn-remove-attachment',
  )
  const buttonUploadNow = pendingAttachmentTr.querySelector(
    '.hyper-btn-upload-attachment',
  )
  if (buttonRemove) {
    configureButtonRemove(buttonRemove, config)
  }
  if (buttonUploadNow) {
    configureButtonUpload(
      buttonUploadNow,
      file,
      category,
      config,
      pendingAttachmentTr,
    )
    handlePendingUploadAll()
  }
  const spinner = pendingAttachmentTr.querySelector(
    'div.spinner',
  ) as HTMLElement
  if (spinner) {
    spinner.style.display = 'none'
  }
}

const renderPendingUpload = async (
  file: File,
  category: HTMLOptionElement,
  config: IAttachmentConfig,
  pendingAttachmentTr: HTMLTableRowElement,
) => {
  const pendingUploadPath =
    '/partner/applications/attachments/render_pending_upload'
  if (file) {
    const pendingUploadAttachmentHtml = await getPendingUploadHtml(
      file.name,
      file.size,
      category.value,
      pendingUploadPath,
    )
    if (pendingUploadAttachmentHtml) {
      const pendingUploadAttachmentTr = document.createElement('tr')
      pendingUploadAttachmentTr.innerHTML = pendingUploadAttachmentHtml

      const tbody = pendingAttachmentTr.parentElement?.closest('tbody')
      if (tbody) {
        const lastPendingAttachmentTr = getLastPendingAttachmentTr()
        tbody.insertBefore(
          pendingUploadAttachmentTr,
          lastPendingAttachmentTr!.nextSibling,
        )
      }
      pendingAttachmentTr.remove()
      renderPendingUploadAlert()
      configurePendingUploadAttachmentTr(
        pendingUploadAttachmentTr,
        file,
        category.value,
        config,
      )
    }
  }
}

const configureSelectCategory = (
  select: HTMLSelectElement,
  file: File,
  config: IAttachmentConfig,
) => {
  select.addEventListener('change', (e) => {
    const selectedOption = e.target as HTMLOptionElement
    const pendingAttachmentTr = select.parentElement?.closest(
      'tr',
    ) as HTMLTableRowElement
    renderPendingUpload(file, selectedOption, config, pendingAttachmentTr)
  })
}

const configurePendingAttachmentTr = (
  pendingAttachmentTr: HTMLTableRowElement,
  file: File,
  config: IAttachmentConfig,
) => {
  const selectCategory = pendingAttachmentTr.querySelector('select')
  if (selectCategory) {
    configureSelectCategory(selectCategory, file, config)
  }

  const buttonRemove = pendingAttachmentTr.querySelector('button')
  if (buttonRemove) {
    configureButtonRemove(buttonRemove, config)
  }

  const spinner = pendingAttachmentTr.querySelector(
    'div.spinner',
  ) as HTMLElement
  if (spinner) {
    spinner.style.display = 'none'
  }
}

export const getPendingAttachmentHtml = async (
  filename: string,
  filesize: number,
  path: string,
) => {
  try {
    const response = await axios.get(path, {
      params: {
        filename,
        filesize,
      },
    })
    return response.data
  } catch (e: unknown) {
    console.error(e)
    const error = e as IErrorResponse
    if (
      error.response?.status === 400 &&
      error.response.data &&
      typeof error.response.data === 'string'
    ) {
      addToast(error.response.data)
    } else {
      addToast('Oops, something went wrong. Please try again.')
    }
  }

  return null
}

const renderPendingAttachment = async (
  file: File,
  config: IAttachmentConfig,
) => {
  const pendingAttachmentHtml = await getPendingAttachmentHtml(
    file.name,
    file.size,
    config.pendingAttachmentPath,
  )
  if (pendingAttachmentHtml) {
    const pendingAttachmentTr = document.createElement('tr')
    pendingAttachmentTr.innerHTML = pendingAttachmentHtml

    const tbody = document.querySelector('.hyper-table-attachments')
    if (tbody) {
      tbody.insertBefore(pendingAttachmentTr, tbody.firstChild)
      // remove "no attachment" <tr>, if one exists.
      tbody.querySelector('tr>td[colspan="100%"]')?.parentElement?.remove()
    }
    configurePendingAttachmentTr(pendingAttachmentTr, file, config)
  }
}

const onFilesAccepted = (files: Array<File>, config: IAttachmentConfig) => {
  files.forEach((file) => {
    renderPendingAttachment(file, config).then(() =>
      renderPendingAlert(getPendingAttachmentCount()),
    )
  })
}

export const onFilesRejected = (files: Array<File>) => {
  console.error('Attachment rejected', files)
  const filenames = files.map((f) => f.name)
  addToast(
    `${pluralize(filenames.length, 'file')} rejected: [${filenames.join(', ')}]`,
  )
}

export const configureAttachments = (config: IAttachmentConfig) => {
  const dnd = document.getElementById('drag-and-drop')
  if (dnd) {
    configureDragAndDrop(dnd, onFilesAccepted, onFilesRejected, config)
  }
  ;[
    ...document.querySelectorAll('[id^="confirm-attachment-delete-modal-"]'),
  ].forEach((element) => {
    configureConfirmAttachmentDeleteModal(element, config)
  })
  document.querySelectorAll('.sync-button').forEach((element) => {
    if (config.resyncAttachmentPath) {
      configureResyncAttachment(element, config.resyncAttachmentPath)
    }
  })
  // Warn user before leaving the page if they have pending attachments or have uploading attachments.
  window.addEventListener('beforeunload', (e) => {
    if (getPendingAttachmentCount() > 0 || clickUploadAll) {
      e.preventDefault()
      e.returnValue = '' // required for Chrome
    }
  })
}
